import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useMemo, useCallback} from 'react'
import type {IdToPullRequestMap} from '../types/pull'
import type {Session} from '../types/session'
import type {BaseTask, HydratedTask, OptimisticTask, UITask, UnhydratedTask} from '../types/task'
import {fetchCAPI} from '../utils/fetch-capi'
import {mergeServerTasksWithOptimistic, transformAPITasksToUnhydrated} from '../utils/task-helpers'
import {useCAPIQuery} from './useCAPIQuery'
import {useLocalStorageCache} from './useLocalStorageCache'
import {usePullRequests} from './usePullRequests'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDebounce} from '@github-ui/use-debounce'
import {tasksApiPathWithParams, type TasksQueryParams} from '../utils/api-url-helpers'
import type {APITask} from '../types/api-task'
import {useRepoInfo} from './use-repo-info'
import {parsePageNumber} from '../utils/url-helpers'
import {useSearchParams} from '@github-ui/use-navigate'
import type {FlatTaskList} from './use-create-api-task'

export const agentTasksQueryKey = ['agent-tasks']
export const TASKS_STORAGE_KEY = 'AGENT_SESSIONS_TASKS'
export const PAGE_SIZE = 10

/**
 * Get the query key for agent tasks with pagination parameters.
 * When using the task API, the query key includes page and archived status.
 * @param useTaskApi - Whether the task API feature flag is enabled
 * @param page - Page number (default: 1)
 * @param fetchArchivedTasks - Whether to fetch archived tasks (default: false for open tasks)
 */
export function getAgentTasksQueryKey(useTaskApi: boolean, page = 1, fetchArchivedTasks?: boolean): readonly unknown[] {
  return useTaskApi ? [...agentTasksQueryKey, page, fetchArchivedTasks] : agentTasksQueryKey
}

function collateSessionsIntoTasks(sessions: Session[]): UnhydratedTask[] {
  const resourceIdToTaskMap = new Map<string, UnhydratedTask>()

  for (const session of sessions) {
    const task = resourceIdToTaskMap.get(session.resource_id.toString())
    if (task) {
      task.session_count += 1
      if (new Date(session.last_updated_at) > new Date(task.latest_session_updated_at)) {
        task.latest_session_updated_at = session.last_updated_at
        task.latest_session_state = session.state
        task.latest_session_id = session.id
        task.agentId = session.agent_id?.toString()
      }
      if (session.event_identifiers) {
        // Include all event_identifiers to enable task de-duping with cached sessions
        const existingIdentifiers = new Set(task.event_identifiers || [])
        const newIdentifiers = session.event_identifiers.filter(id => !existingIdentifiers.has(id))
        task.event_identifiers = [...(task.event_identifiers || []), ...newIdentifiers]
      }
    } else {
      resourceIdToTaskMap.set(session.resource_id.toString(), {
        resource_type: session.resource_type,
        resource_id: session.resource_id,
        resource_global_id: session.resource_global_id,
        session_count: 1,
        latest_session_updated_at: session.last_updated_at,
        latest_session_state: session.state,
        latest_session_id: session.id,
        event_identifiers: session.event_identifiers,
        event_type: session.event_type ?? undefined,
        agentId: session.agent_id?.toString(),
        task_id: session.task_id,
        creator_id: session.owner_id,
      })
    }
  }

  return Array.from(resourceIdToTaskMap.values()).sort((a, b) => {
    return new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime()
  })
}

export type TaskCounts = {
  open: number
  closed: number
}

/**
 * Response tuple from fetchTasks:
 * - tasks list
 * - has_next_page boolean
 * - task counts (open/closed)
 */
export type FetchTasksResponse = [BaseTask[], boolean, TaskCounts?]

/**
 * Extract the flat task list from the FetchTasksResponse tuple.
 * Used with useCreateAPITask for optimistic updates.
 */
export function getFlatTaskList(data: FetchTasksResponse | undefined): FlatTaskList | undefined {
  return data?.[0] as FlatTaskList | undefined
}

/**
 * Create a new FetchTasksResponse with an updated task list.
 * Used with useCreateAPITask for optimistic updates.
 */
export function setFlatTaskList(oldData: FetchTasksResponse | undefined, newList: FlatTaskList): FetchTasksResponse {
  return [newList, oldData?.[1] ?? false, oldData?.[2]]
}

export const fetchTasks = async ({
  queryClient,
  setCachedTasks,
  copilotApiUrl,
  queryParams,
  useTaskApi = false,
  queryKey,
}: {
  queryClient: QueryClient
  setCachedTasks: (tasks: BaseTask[]) => void
  copilotApiUrl?: string | undefined
  queryParams?: TasksQueryParams
  useTaskApi?: boolean
  queryKey?: readonly unknown[]
}): Promise<FetchTasksResponse> => {
  let path = '/agents/sessions'
  if (useTaskApi) {
    path = tasksApiPathWithParams(queryParams)
  }

  const response = await fetchCAPI({
    path,
    copilotApiUrl,
  })
  const data = await response.json()

  let serverTasks: UnhydratedTask[]
  if (useTaskApi) {
    const apiTasks = data.tasks as APITask[]
    serverTasks = transformAPITasksToUnhydrated(apiTasks)
  } else {
    const sessions = data.sessions
    serverTasks = collateSessionsIntoTasks(sessions as Session[])
  }

  // Get existing tasks from cache so that we can find and preserve optimistic tasks until next poll
  // Use the provided queryKey to read from the correct cache (includes page/archived params when using task API)
  const existingTasks = queryClient.getQueryData<FetchTasksResponse>(queryKey ?? agentTasksQueryKey)?.[0] || []

  // Merge server tasks with existing optimistic tasks
  const optimisticTasks = existingTasks.filter(task => task.isOptimistic === true) as OptimisticTask[]
  const mergedTasks = mergeServerTasksWithOptimistic(serverTasks, optimisticTasks)

  // Cache the merged tasks to localStorage
  setCachedTasks(mergedTasks)

  // Extract pagination and counts from response (only available with task API)
  const hasNextPage = !!data.has_next_page
  let taskCounts: TaskCounts | undefined
  if (useTaskApi && typeof data.total_active_count === 'number' && typeof data.total_archived_count === 'number') {
    taskCounts = {
      open: data.total_active_count,
      closed: data.total_archived_count,
    }
  }

  return [mergedTasks, hasNextPage, taskCounts]
}

// TODO: Combine this with the use-repo-tasks hook once we are fully on the task API to avoid duplication
export function useTasks({
  sessionsPollingInterval = 3000,
  initialTasks,
  initialPullRequestsMap,
  fetchArchivedTasks,
  includeCounts,
}: {
  sessionsPollingInterval?: number | false
  initialTasks?: HydratedTask[]
  initialPullRequestsMap?: IdToPullRequestMap
  fetchArchivedTasks?: boolean
  includeCounts?: boolean
} = {}) {
  const {value: cachedTasks, setValue: setCachedTasks} = useLocalStorageCache<BaseTask[]>(TASKS_STORAGE_KEY)
  const queryClient = useQueryClient()
  const isAliveEnabled = isFeatureEnabled('copilot_agent_sessions_alive_updates')
  const useTaskApi = isFeatureEnabled('copilot_mission_control_use_task_api_outside_of_repos')
  const [searchParams] = useSearchParams()
  const currentPage = parsePageNumber(searchParams.get('page'))

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: agentTasksQueryKey,
    })
    queryClient.invalidateQueries({
      queryKey: ['agent-sessions-pull-requests'],
    })
  }, [queryClient])

  const debouncedHandleAliveUpdate = useDebounce(
    handleAliveUpdate,
    sessionsPollingInterval ? sessionsPollingInterval : 3000,
    {maxWait: 10000},
  )

  // Subscribe to alive updates if feature flag is enabled
  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enabled: isAliveEnabled,
  })

  const queryParams: TasksQueryParams = {
    archived: fetchArchivedTasks,
    includeCounts: useTaskApi ? includeCounts : undefined,
    page: useTaskApi ? currentPage : undefined,
    pageSize: useTaskApi ? PAGE_SIZE : undefined,
  }

  // Include page and archived in query key for proper cache separation
  const queryKey = useTaskApi ? [...agentTasksQueryKey, currentPage, fetchArchivedTasks] : agentTasksQueryKey

  const query = useCAPIQuery<FetchTasksResponse>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async () =>
      await fetchTasks({
        queryClient,
        setCachedTasks,
        queryParams,
        useTaskApi,
        queryKey,
      }),
    initialData: initialTasks
      ? [initialTasks, false, undefined]
      : cachedTasks
        ? [cachedTasks, false, undefined]
        : undefined,
    refetchInterval: isAliveEnabled ? false : sessionsPollingInterval,
    refetchIntervalInBackground: false,
  })

  // Get all tasks from the query result (already includes optimistic tasks)
  const allTasks = useMemo(() => query.data?.[0] || [], [query.data])
  const hasNextPage = query.data?.[1] ?? false
  const counts = query.data?.[2]

  // Get pull requests for tasks that have valid, non-optimistic resource IDs
  const tasksWithResourceIds = allTasks.filter(task => task.resource_id !== undefined && task.resource_id !== -1)
  const {
    data: idToPullRequestMap,
    isError: isErrorPullRequests,
    isFetching: isFetchingPullRequests,
    isLoading: isLoadingPullRequests,
  } = usePullRequests({
    tasks: tasksWithResourceIds,
    sessionsPollingInterval,
    initialPullRequestsMap,
  })

  // Get repo IDs for tasks without pull requests (only when using task API)
  const repoIdsNeedingFetch = useMemo(() => {
    if (!useTaskApi) return []
    // Use initialPullRequestsMap when available (hard nav) for parallel loading with PR fetching
    const prMap = initialPullRequestsMap ?? idToPullRequestMap
    const ids: number[] = []
    for (const task of allTasks) {
      if (task.isOptimistic) continue
      if (task.resource_id && prMap[task.resource_id]) continue // Has PR, don't need to fetch repo info
      if (task.repo_id) {
        ids.push(task.repo_id)
      }
    }
    return ids
  }, [useTaskApi, allTasks, initialPullRequestsMap, idToPullRequestMap])

  // Fetch repo info for tasks without pull requests
  const {data: repoMap, isLoading: isLoadingRepoInfo, isError: isErrorRepoInfo} = useRepoInfo(repoIdsNeedingFetch)

  // Attach pull request data to tasks where available, but only return hydrated or optimistic tasks
  // Memoize to prevent unnecessary re-renders of task list items when data hasn't changed
  const returnTasks = useMemo(() => {
    return allTasks
      .map(task => {
        const pull = task.resource_id ? idToPullRequestMap[task.resource_id] : undefined

        if (pull) {
          return {
            ...task,
            isOptimistic: false, // Be sure to always mark any transitional tasks that have pull requests as no longer optimistic
            pull,
            nwo: pull.repository_nwo,
          }
        } else {
          // If no pull request is found, return the task without pull data but include repo nwo
          const repo = task.repo_id ? repoMap[task.repo_id] : undefined
          return {
            ...task,
            pull: undefined,
            nwo: repo ? `${repo.ownerLogin}/${repo.name}` : task.nwo,
          }
        }
      })
      .filter(task => {
        // Always include optimistic tasks
        if (task.isOptimistic === true) {
          return true
        }

        // When using old sessions API, only include tasks with PR data (original behavior)
        // When using new task API, also include tasks with nwo (tasks without PRs yet)
        if (useTaskApi) {
          return task.pull !== undefined || task.nwo !== undefined
        }

        return task.pull !== undefined
      }) as UITask[]
  }, [allTasks, idToPullRequestMap, repoMap, useTaskApi])

  // Show loading when tasks, pull requests, or repo info are loading (for initial load)
  const isLoading =
    (query.isLoading && !query.data) ||
    (isLoadingPullRequests && !initialPullRequestsMap && allTasks.length > 0) ||
    (useTaskApi && isLoadingRepoInfo && repoIdsNeedingFetch.length > 0 && allTasks.length > 0)

  const isError = query.isError || isErrorPullRequests || isErrorRepoInfo

  // Show fetching state when fetching tasks or pull requests
  const isFetching = query.isFetching || (isFetchingPullRequests && allTasks.length > 0)

  return {
    tasks: returnTasks,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    counts,
  }
}
